import { zodResolver } from "@hookform/resolvers/zod";
import { convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import useApiRequest from "../../lib/hooks/useRequest";
import { convertHtmlToEditorState, parseTemplateCategory } from "../../lib/hooks/utils";
import {
  CreateTemplateRequestDto,
  TemplateCategory,
  TemplateCreationEntitiesResponseDto,
  TemplateDetailsResponseDto,
} from "../../lib/interfaces/template";
import { Button } from "../../ui/components/ui/button";
import DataFetchParent from "../../ui/components/ui/data-fetch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../ui/components/ui/form";
import { Input } from "../../ui/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/components/ui/select";
import { Textarea } from "../../ui/components/ui/textarea";
import CreateEditor from "../order/create/create-editor";

const TemplateForm = ({
  template,
  submitCallback,
}: {
  template?: TemplateDetailsResponseDto;
  submitCallback?: () => void;
}) => {
  const { apiRequest, apiFileRequest } = useApiRequest();
  const [editorState, setEditorState] = useState(() => convertHtmlToEditorState(template?.mockDescription || ""));
  const [creationEntities, setCreationEntities] = useState<TemplateCreationEntitiesResponseDto>();
  const [loading, setLoading] = useState(false);

  const fetchCreationEntities = useCallback(async () => {
    setLoading(true);
    const res = await apiRequest<TemplateCreationEntitiesResponseDto>("templates/internal/creation-entities", "GET");
    setCreationEntities(res.data);
    setLoading(false);
  }, [apiRequest]);

  useEffect(() => {
    fetchCreationEntities();
  }, [fetchCreationEntities]);

  const createTemplateSchema = z.object({
    name: z
      .string()
      .max(100, {
        message: "Der Name darf höchstens 100 Zeichen lang sein",
      })
      .min(1, {
        message: "Template-Name ist erforderlich",
      }),
    description: z
      .string()
      .max(500, {
        message: "Die Beschreibung darf höchstens 500 Zeichen lang sein",
      })
      .min(10, {
        message: "Die Beschreibung muss mindestens 10 Zeichen lang sein",
      }),
    category: z.nativeEnum(TemplateCategory, {
      errorMap: () => ({ message: "Bitte wählen Sie eine Kategorie aus" }),
    }),
    isFeatured: z.boolean().default(false),
    mockTitle: z
      .string()
      .max(45, {
        message: "Der Auftragstitel darf höchstens 45 Zeichen lang sein",
      })
      .min(1, {
        message: "Template-Name ist erforderlich",
      }),
    mockDescription: z.string().refine(
      () => {
        const htmlContentProfile = draftToHtml(convertToRaw(editorState.getCurrentContent()));
        return htmlContentProfile.length > 2;
      },
      { message: "Beschreibung ist erforderlich" }
    ),
    imageFile: z
    .instanceof(File, { message: "Bitte wählen Sie ein Bild aus" })
    .refine((file) => {
      if (file.name === 'internalerrorfilename') {
        return false;
      }
      return true;
    }, "Bitte wählen Sie ein Bild aus")
    .refine((file) => {
      if (file.size === 0 && file.name.startsWith('http')) {
        return true;
      }
      return file.size <= 5000000;
    }, "Das Bild darf höchstens 5MB groß sein")
    .refine(
      (file) => {
        if (file.size === 0 && file.name.startsWith('http')) {
          return true;
        }
        return ["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type);
      },
      "Nur JPEG, PNG und WebP Dateien sind erlaubt"
    ),
  });

  type CreateTemplateSchema = z.infer<typeof createTemplateSchema>;

  const form = useForm<CreateTemplateSchema>({
    resolver: zodResolver(createTemplateSchema),
    defaultValues: {
      name: template?.title || "",
      description: template?.description || "",
      category: template?.templateCategory || TemplateCategory.MARKETING,
      isFeatured: template?.featured || false,
      mockTitle: template?.mockTitle || "",
      mockDescription: template?.mockDescription || "",
      imageFile: template?.imageUrl ? new File([], template.imageUrl) : undefined,
    },
  });

  const onSubmit = async (data: CreateTemplateSchema) => {
    const contentState = editorState.getCurrentContent();
    const rawContentState = convertToRaw(contentState);
    const htmlContent = draftToHtml(rawContentState);
    const request: CreateTemplateRequestDto = {
      title: data.name,
      description: data.description,
      templateCategory: data.category,
      featured: data.isFeatured,
      mockTitle: data.mockTitle,
      mockDescription: htmlContent,
    };

    if (template) {
      await apiRequest(`templates/internal/${template._id}`, "PATCH", {
        body: request,
        toast: { toastText: "Vorlage wurde erfolgreich bearbeitet" },
      });
      if (data.imageFile && data.imageFile.size > 0) {
        await apiFileRequest(`templates/internal/${template._id}/image`, "POST", data.imageFile, {
          formdataName: "file",
          toast: { toastText: "Vorlagenbild wurde erfolgreich bearbeitet" },
        });
      }
    } else {
      const response = await apiRequest<TemplateDetailsResponseDto>("templates/internal", "POST", {
        body: request,
        toast: { toastText: "Vorlage wurde erfolgreich erstellt" },
      });
      if (response.status < 300 && response.status >= 200) {
        if (data.imageFile && response.data && response.data._id) {
          await apiFileRequest(`templates/internal/${response.data._id}/image`, "POST", data.imageFile, {
            formdataName: "file",
            toast: { toastText: "Vorlagenbild wurde erfolgreich erstellt" },
          });
        }
      }
    }
    if (submitCallback) {
      submitCallback();
    }
  };

  const deleteImage = async () => {
    if (template && form.watch("imageFile").size === 0 && form.watch("imageFile").name.startsWith('http')) {
      try {
        await apiRequest(`templates/internal/${template?._id}/image`, "DELETE", {
          toast: { toastText: "Vorlagenbild wurde erfolgreich gelöscht" },
        });
      } catch (error) {
        console.error("Error deleting image:", error);
      }
    }
    form.setValue("imageFile", new File([], "internalerrorfilename"));
    form.trigger("imageFile");
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6 h-full">
        <div className="flex flex-col gap-3">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white p-2 rounded-lg gap-3">
                <FormLabel className="text-[16px] font-bold text-[#12282A]">Template-Name:*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Name der Vorlage" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col gap-3">
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white p-2 rounded-lg gap-3">
                <FormLabel className="text-[16px] font-bold text-[#12282A]">Beschreibung:*</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Beschreibung der Vorlage..."
                    className="min-h-[100px] resize-none"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col gap-3">
          <FormField
            control={form.control}
            name="mockTitle"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white p-2 rounded-lg gap-3">
                <FormLabel className="text-[16px] font-bold text-[#12282A]">Auftragstitel:*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Name der Vorlage" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col gap-3">
          <FormField
            control={form.control}
            name="mockDescription"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white p-2 rounded-lg gap-3">
                <FormLabel className="text-[16px] font-bold text-[#12282A]">Auftragsbeschreibung:*</FormLabel>
                <FormControl>
                  <CreateEditor editorState={editorState} setEditorState={setEditorState} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex gap-3">
          <DataFetchParent
            isLoading={loading}
            data={creationEntities}
            renderElement={(data: TemplateCreationEntitiesResponseDto) => (
              <div className="w-1/2">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem className="flex flex-col bg-white p-2 rounded-lg gap-3">
                      <FormLabel className="text-[16px] font-bold text-[#12282A]">Kategorie:*</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Kategorie auswählen" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {data.categories.map((category) => (
                            <SelectItem key={category} value={category}>
                              {parseTemplateCategory(category)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          />

          <div className="w-1/2">
            <FormField
              control={form.control}
              name="imageFile"
              render={({ field: { onChange, value, ...field } }) => (
                <FormItem className="flex flex-col bg-white p-2 rounded-lg gap-3">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Vorschaubild:*</FormLabel>
                  {form.watch("imageFile") && form.watch("imageFile").name !== "" && form.watch("imageFile").name !== "internalerrorfilename" ? (
                    <>
                      <img
                        src={
                          form.watch("imageFile").size > 0
                            ? URL.createObjectURL(form.watch("imageFile"))
                            : template?.imageUrl
                            ? template.imageUrl
                            : ""
                        }
                        alt="Vorschaubild"
                        className="w-full h-full object-cover"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          deleteImage();
                        }}
                      >
                        Vorschaubild löschen
                      </Button>
                    </>
                  ) : (
                    <FormControl>
                      <Input
                        {...field}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            onChange(file);
                          }
                        }}
                      />
                    </FormControl>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <FormField
            control={form.control}
            name="isFeatured"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-y-0 space-x-2">
                <FormControl>
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={field.onChange}
                    className="h-4 w-4 rounded border border-gray-300"
                  />
                </FormControl>
                <FormLabel className="text-[14px] font-medium text-[#12282A]">Hervorheben</FormLabel>
              </FormItem>
            )}
          />
        </div>

        <Button
          type="submit"
          variant="default"
          className="text-white font-bold flex items-center justify-center gap-4 w-full mt-4"
        >
          <span>{template ? "Vorlage bearbeiten" : "Vorlage erstellen"}</span>
        </Button>
      </form>
    </Form>
  );
};

export default TemplateForm;
